/**
 * AdvancedFiltersPanel - Slide-in panel for comprehensive task filtering
 *
 * Features:
 * - Preview mode: Shows count of matching results before applying
 * - Collapsible filter sections with active filter badges
 * - Task Type hierarchical filter with group-level "Select All"
 * - Reusable across all task verticals (Claims, Intake, Medical, etc.)
 *
 * Preview/Apply Pattern:
 * - As user selects filters, a preview query runs in the background
 * - "View X results" button shows the preview count
 * - On click, filters are applied to the main table
 */

import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { RainmakerLogo } from '@/components/rainmaker';
import { Button } from '@/components/ui/catalyst/button';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import type {
  TaskCountsResponse,
  TaskFilterOption,
} from '@/services/api/tasksService';
import { useGetTasksQuery } from '@/services/api/tasksService';
import { FilterRecord } from '@/services/types/pagination-types';
import { ensureArray } from '@/utils/pagination';

import { ADVANCED_FILTER_KEYS } from '../../config/filterConfig';
import { useDateRangeFilter } from '../../hooks/useDateRangeFilter';
import {
  getRainmakerFilterUpdates,
  getRainmakerMode,
  RainmakerMode,
} from '../../utils/filterUtils';
import { ChipFilterGroup } from './ChipFilterGroup';
import { CollapsibleFilterSection } from './CollapsibleFilterSection';
import { DueDateFilter } from './DueDateFilter';
import { SearchableCheckboxList } from './SearchableCheckboxList';
import { SpecialFilterToggle } from './SpecialFilterToggle';
import { TaskTypeFilter } from './TaskTypeFilter';

interface AdvancedFiltersPanelProps {
  isOpen: boolean;
  onClose: () => void;
  filters: FilterRecord;
  onFiltersChange: (filters: FilterRecord) => void;
  // Metadata for filter options
  groupCounts?: TaskCountsResponse;
  firmOptions?: TaskFilterOption[];
  assigneeOptions?: TaskFilterOption[];
  caseStatusOptions?: TaskFilterOption[];
  priorityOptions?: TaskFilterOption[];
  lastUpdateOptions?: TaskFilterOption[];
  statusOptions?: TaskFilterOption[];
  coverageOptions?: TaskFilterOption[];
  // Base filters for the vertical (to include in preview query)
  baseFilters?: FilterRecord;
}

export const AdvancedFiltersPanel = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  groupCounts,
  firmOptions = [],
  assigneeOptions = [],
  caseStatusOptions = [],
  priorityOptions = [],
  lastUpdateOptions = [],
  statusOptions = [],
  coverageOptions = [],
  baseFilters = {},
}: AdvancedFiltersPanelProps) => {
  // Local state for pending filter changes (preview mode)
  const [pendingFilters, setPendingFilters] = useState<FilterRecord>(filters);

  // Sync pending filters when panel opens or external filters change
  useEffect(() => {
    if (isOpen) {
      setPendingFilters(filters);
    }
  }, [isOpen, filters]);

  // Build query params for preview (combine base filters + pending filters)
  const previewQueryParams = useMemo(() => {
    const combined: Record<string, unknown> = { ...baseFilters };

    // Apply pending filters
    Object.entries(pendingFilters).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (Array.isArray(value) && value.length > 0) {
          combined[key] = value;
        } else if (typeof value === 'string' && value.length > 0) {
          combined[key] = value;
        } else if (typeof value === 'boolean') {
          combined[key] = value;
        }
      }
    });

    return {
      ...combined,
      limit: 1, // Only need count, not actual data
      offset: 0,
    };
  }, [baseFilters, pendingFilters]);

  // Preview query - fetches count with pending filters
  const { data: previewData, isFetching: isPreviewLoading } = useGetTasksQuery(
    previewQueryParams,
    {
      skip: !isOpen,
    },
  );

  const previewCount = previewData?.count ?? 0;

  // Handler to update a single filter
  const handleFilterChange = useCallback(
    (key: string, value: string[] | string | null) => {
      setPendingFilters((prev) => ({
        ...prev,
        [key]: value as string | string[], // Cast assuming UI filters are strings/arrays
      }));
    },
    [],
  );

  // Handler for Task Type filter (updates both canonical_types and canonical_groups)
  const handleTaskTypeChange = useCallback(
    (types: string[], groups: string[]) => {
      setPendingFilters((prev) => {
        const next = { ...prev };

        if (types.length > 0) {
          next[ADVANCED_FILTER_KEYS.TASK_TYPES] = types;
        } else {
          // Explicitly set to null to signal removal
          next[ADVANCED_FILTER_KEYS.TASK_TYPES] = null;
        }

        if (groups.length > 0) {
          next[ADVANCED_FILTER_KEYS.TASK_GROUPS] = groups;
        } else {
          // Explicitly set to null to signal removal
          next[ADVANCED_FILTER_KEYS.TASK_GROUPS] = null;
        }

        return next;
      });
    },
    [],
  );

  // Handler for Rainmaker mode toggle
  const handleRainmakerModeChange = useCallback((mode: RainmakerMode) => {
    setPendingFilters((prev) => ({
      ...prev,
      ...getRainmakerFilterUpdates(mode),
    }));
  }, []);

  // Date range filter with mutual exclusivity (hook handles the logic)
  // Uses 'due_date' config which includes the "no due date" option
  const {
    handleDateRangeChange: handleDueDateFilterChange,
    handleNoDueDateChange: handleNoDueDateToggle,
    startDate: dueDateRangeStart,
    endDate: dueDateRangeEnd,
    noDueDate: noDueDateValue,
  } = useDateRangeFilter({
    filters: pendingFilters,
    setFilters: setPendingFilters,
    configKey: 'due_date',
  });

  // Clear all filters
  const handleClearAll = useCallback(() => {
    setPendingFilters({});
  }, []);

  // Apply filters and close panel
  const handleApply = useCallback(() => {
    // Clean up empty arrays but preserve explicit null values
    // Null values are set by special filter handlers (e.g., getSpeedboatFilterUpdates)
    // to signal deletion of mutually exclusive filter keys
    const cleanedFilters: FilterRecord = {};
    Object.entries(pendingFilters).forEach(([key, value]) => {
      if (value === null) {
        // Preserve explicit nulls - they signal deletion
        cleanedFilters[key] = value;
      } else if (Array.isArray(value)) {
        if (value.length > 0) {
          cleanedFilters[key] = value;
        } else {
          // Convert empty arrays to null to signal deletion
          cleanedFilters[key] = null;
        }
      } else if (typeof value === 'string') {
        if (value.length > 0) {
          cleanedFilters[key] = value;
        } else {
          // Convert empty strings to null to signal deletion
          cleanedFilters[key] = null;
        }
      } else if (typeof value === 'boolean') {
        cleanedFilters[key] = value;
      }
    });

    onFiltersChange(cleanedFilters);
    onClose();
  }, [pendingFilters, onFiltersChange, onClose]);

  // Check if there are any active filters
  const hasActiveFilters = useMemo(() => {
    return Object.values(pendingFilters).some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      return Boolean(value);
    });
  }, [pendingFilters]);

  // Get current values for each filter type
  // Helper to safely extract array values from loose filter record
  const getFilterValues = (key: string): string[] => {
    return (
      ensureArray(pendingFilters[key] as string | string[] | undefined) || []
    );
  };

  const selectedTaskTypes = getFilterValues(ADVANCED_FILTER_KEYS.TASK_TYPES);
  const selectedGroups = getFilterValues(ADVANCED_FILTER_KEYS.TASK_GROUPS);
  const selectedPriorities = getFilterValues(ADVANCED_FILTER_KEYS.PRIORITY);
  const selectedStatuses = getFilterValues(ADVANCED_FILTER_KEYS.STATUS);
  const selectedFirms = getFilterValues(ADVANCED_FILTER_KEYS.FIRM);
  const selectedCoverages = getFilterValues(
    ADVANCED_FILTER_KEYS.COVERAGE_CARRIER,
  );
  const selectedAssignees = getFilterValues(ADVANCED_FILTER_KEYS.ASSIGNEE);
  const selectedCaseStatuses = getFilterValues(
    ADVANCED_FILTER_KEYS.CASE_STATUS,
  );
  const selectedLastUpdatePresets = getFilterValues(
    ADVANCED_FILTER_KEYS.LAST_UPDATE_RANGE,
  );

  // Derive current rainmaker mode from filter state
  const currentRainmakerMode: RainmakerMode = useMemo(
    () =>
      getRainmakerMode(
        pendingFilters as Record<
          string,
          string | string[] | boolean | null | undefined
        >,
      ),
    [pendingFilters],
  );

  // Use API options when available, fallback to empty arrays
  const effectivePriorityOptions =
    priorityOptions.length > 0 ? priorityOptions : [];

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-[400px] sm:max-w-[400px] flex flex-col p-0"
      >
        {/* Header - Note: Sheet component provides the X close button */}
        <SheetHeader className="px-6 py-3 border-b border-zinc-950/10 dark:border-white/10 flex-shrink-0">
          <div className="flex items-center gap-2">
            <SheetTitle className="text-sm font-semibold text-zinc-950 dark:text-white">
              Advanced Filters
            </SheetTitle>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
        </SheetHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="divide-y divide-zinc-950/10 dark:divide-white/10">
            {/* Special Filters - Rainmaker */}
            <CollapsibleFilterSection
              title="Special Filters"
              activeCount={currentRainmakerMode !== 'all' ? 1 : 0}
              defaultOpen={false}
            >
              <div className="space-y-3">
                <SpecialFilterToggle
                  label="Rainmaker"
                  icon={
                    <RainmakerLogo
                      size={16}
                      className="text-violet-500 dark:text-violet-400"
                    />
                  }
                  mode={currentRainmakerMode}
                  onModeChange={handleRainmakerModeChange}
                />
              </div>
            </CollapsibleFilterSection>

            {/* Task Type Filter */}
            <CollapsibleFilterSection
              title="Task Type"
              activeCount={selectedTaskTypes.length}
              defaultOpen={selectedTaskTypes.length > 0}
            >
              <TaskTypeFilter
                groupCounts={groupCounts}
                selectedTypes={selectedTaskTypes}
                selectedGroups={selectedGroups}
                onChange={handleTaskTypeChange}
              />
            </CollapsibleFilterSection>

            {/* Remaining filters in alphabetical order */}

            {/* Assignee Filter */}
            <CollapsibleFilterSection
              title="Assignee"
              activeCount={selectedAssignees.length}
              defaultOpen={selectedAssignees.length > 0}
            >
              {assigneeOptions.length > 0 ? (
                <SearchableCheckboxList
                  options={assigneeOptions}
                  selectedValues={selectedAssignees}
                  onChange={(values) =>
                    handleFilterChange(ADVANCED_FILTER_KEYS.ASSIGNEE, values)
                  }
                  placeholder="Search assignees..."
                />
              ) : (
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  No assignees available
                </p>
              )}
            </CollapsibleFilterSection>

            {/* Case Status Filter */}
            <CollapsibleFilterSection
              title="Case Status"
              activeCount={selectedCaseStatuses.length}
              defaultOpen={selectedCaseStatuses.length > 0}
            >
              {caseStatusOptions.length > 0 ? (
                <ChipFilterGroup
                  options={caseStatusOptions}
                  selectedValues={selectedCaseStatuses}
                  onChange={(values) =>
                    handleFilterChange(ADVANCED_FILTER_KEYS.CASE_STATUS, values)
                  }
                />
              ) : (
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  No case statuses available
                </p>
              )}
            </CollapsibleFilterSection>

            {/* Due Date Filter */}
            <CollapsibleFilterSection
              title="Next Action Date"
              activeCount={
                (noDueDateValue ? 1 : 0) +
                (dueDateRangeStart || dueDateRangeEnd ? 1 : 0)
              }
              defaultOpen={
                noDueDateValue || !!dueDateRangeStart || !!dueDateRangeEnd
              }
            >
              <DueDateFilter
                customRangeStart={dueDateRangeStart}
                customRangeEnd={dueDateRangeEnd}
                noDueDateValue={noDueDateValue}
                onCustomRangeChange={handleDueDateFilterChange}
                onNoDueDateChange={handleNoDueDateToggle}
              />
            </CollapsibleFilterSection>

            {/* Coverage Carrier Filter */}
            {(coverageOptions?.length ?? 0) > 0 && (
              <CollapsibleFilterSection
                title="Coverage Carrier"
                activeCount={selectedCoverages.length}
                defaultOpen={selectedCoverages.length > 0}
              >
                <SearchableCheckboxList
                  options={coverageOptions || []}
                  selectedValues={selectedCoverages}
                  onChange={(values) =>
                    handleFilterChange(
                      ADVANCED_FILTER_KEYS.COVERAGE_CARRIER,
                      values,
                    )
                  }
                  placeholder="Search carriers..."
                />
              </CollapsibleFilterSection>
            )}

            {/* Firm Filter */}
            <CollapsibleFilterSection
              title="Firm"
              activeCount={selectedFirms.length}
              defaultOpen={selectedFirms.length > 0}
            >
              {firmOptions.length > 0 ? (
                <SearchableCheckboxList
                  options={firmOptions}
                  selectedValues={selectedFirms}
                  onChange={(values) =>
                    handleFilterChange(ADVANCED_FILTER_KEYS.FIRM, values)
                  }
                  placeholder="Search firms..."
                />
              ) : (
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  No firms available
                </p>
              )}
            </CollapsibleFilterSection>

            {/* Last Update Filter */}
            {lastUpdateOptions.length > 0 && (
              <CollapsibleFilterSection
                title="Last Update"
                activeCount={selectedLastUpdatePresets.length}
                defaultOpen={selectedLastUpdatePresets.length > 0}
              >
                <ChipFilterGroup
                  options={lastUpdateOptions}
                  selectedValues={selectedLastUpdatePresets}
                  onChange={(values) =>
                    handleFilterChange(
                      ADVANCED_FILTER_KEYS.LAST_UPDATE_RANGE,
                      values,
                    )
                  }
                />
              </CollapsibleFilterSection>
            )}

            {/* Priority Filter */}
            <CollapsibleFilterSection
              title="Priority"
              activeCount={selectedPriorities.length}
              defaultOpen={selectedPriorities.length > 0}
            >
              <ChipFilterGroup
                options={effectivePriorityOptions}
                selectedValues={selectedPriorities}
                onChange={(values) =>
                  handleFilterChange(ADVANCED_FILTER_KEYS.PRIORITY, values)
                }
              />
            </CollapsibleFilterSection>

            {/* Status Filter */}
            <CollapsibleFilterSection
              title="Status"
              activeCount={selectedStatuses.length}
              defaultOpen={selectedStatuses.length > 0}
            >
              {statusOptions.length > 0 ? (
                <ChipFilterGroup
                  options={statusOptions}
                  selectedValues={selectedStatuses}
                  onChange={(values) =>
                    handleFilterChange(ADVANCED_FILTER_KEYS.STATUS, values)
                  }
                />
              ) : (
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  No status options available
                </p>
              )}
            </CollapsibleFilterSection>
          </div>
        </div>

        {/* Footer with Apply Button */}
        <SheetFooter className="px-6 py-4 border-t border-zinc-950/10 dark:border-white/10 flex-shrink-0">
          <Button
            outline
            className="w-full justify-center !text-xs !font-medium h-8"
            onClick={handleApply}
            disabled={isPreviewLoading}
          >
            {isPreviewLoading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-zinc-600 border-t-transparent" />
                Loading...
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                Apply filters
                <span className="text-zinc-500 dark:text-zinc-400 font-normal">
                  ({previewCount.toLocaleString()} results)
                </span>
              </span>
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

// Export a trigger button component for consistent styling
export const AdvancedFiltersTrigger = ({
  onClick,
  hasActiveFilters,
  activeCount,
}: {
  onClick: () => void;
  hasActiveFilters: boolean;
  activeCount: number;
}) => {
  return (
    <Button
      plain
      onClick={onClick}
      className="relative px-2 border border-zinc-300 dark:border-zinc-700 h-8 w-8 flex items-center justify-center"
    >
      <AdjustmentsHorizontalIcon className="h-4 w-4" />
      <span className="sr-only">Advanced Filters</span>
      {hasActiveFilters && (
        <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-zinc-700 dark:bg-zinc-500 px-1 text-[10px] font-medium text-white">
          {activeCount}
        </span>
      )}
    </Button>
  );
};
